import { useMemo, useState } from "react";
import {
  ShieldCheck,
  Plus,
  Lock,
  Pencil,
  Eye,
  Trash2,
  KeyRound,
  Layers,
  RefreshCw,
} from "lucide-react";
import styles from "./Roles.module.css";
import RoleModal from "../../../components/UsersPermissions/RoleModal";
import DeleteConfirmModal from "../../../components/BaseComponents/DeleteConfirmModal/DeleteConfirmModal";
import { useToast } from "../../../context/ToastContext";
import {
  useRolesList,
  useDeleteRole,
} from "../../../api/users/useRolesApi";
import { usePermissionsList } from "../../../api/users/usePermissionsApi";

const prettyRole = (name = "") =>
  name
    .split("_")
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
    .join(" ");

const hierarchyLabel = (lvl) => {
  if (lvl === 1) return "Level 1 · Highest";
  if (lvl == null) return "Custom";
  return `Level ${lvl}`;
};

const Roles = () => {
  const { showToast } = useToast();
  const { data: roles, loading, error, refetch } = useRolesList();
  const { data: permissions } = usePermissionsList();
  const { execute: deleteRole, loading: deleting } = useDeleteRole();

  const [modalOpen, setModalOpen] = useState(false);
  const [editRole, setEditRole] = useState(null);
  const [readOnly, setReadOnly] = useState(false);
  const [roleToDelete, setRoleToDelete] = useState(null);

  const sortedRoles = useMemo(
    () =>
      [...(roles || [])].sort(
        (a, b) =>
          (a.hierarchyLevel ?? 99) - (b.hierarchyLevel ?? 99) ||
          a.name.localeCompare(b.name)
      ),
    [roles]
  );

  const openCreate = () => {
    setEditRole(null);
    setReadOnly(false);
    setModalOpen(true);
  };

  const openEdit = (role) => {
    setEditRole(role);
    setReadOnly(!!role.isSystem);
    setModalOpen(true);
  };

  const handleRefresh = async () => {
    const res = await refetch();
    if (res?.success === false) showToast(res.message || "Failed to refresh roles", "error");
    else showToast("Roles refreshed", "success");
  };

  const handleSaved = () => {
    setModalOpen(false);
    refetch();
  };

  const handleConfirmDelete = async () => {
    if (!roleToDelete) return;
    try {
      await deleteRole(roleToDelete.id);
      showToast("Role deleted successfully", "success");
      setRoleToDelete(null);
      refetch();
    } catch (err) {
      showToast(err.response?.data?.message || "Failed to delete role", "error");
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>
            <ShieldCheck size={22} className={styles.titleIcon} />
            Roles &amp; Permissions
          </h1>
          <p className={styles.subtitle}>
            Define roles and the permissions each role grants
          </p>
        </div>
        <div className={styles.headerActions}>
          <button className={styles.refreshBtn} onClick={handleRefresh}>
            <RefreshCw size={16} />
            {loading ? "Refreshing…" : "Refresh"}
          </button>
          <button className={styles.addBtn} onClick={openCreate}>
            <Plus size={16} />
            Add Role
          </button>
        </div>
      </div>

      <div className={styles.infoBar}>
        <KeyRound size={15} />
        <span>
          Roles bundle permissions for quick assignment. System roles are locked and
          can be viewed but not modified. Custom roles can be created and edited freely.
        </span>
      </div>

      {error && <div className={styles.error}>{error}</div>}

      {loading ? (
        <div className={styles.loading}>Loading roles…</div>
      ) : (
        <div className={styles.grid}>
          {sortedRoles.map((role) => {
            const permCount = role.Permissions?.length ?? 0;
            return (
              <div key={role.id} className={styles.card}>
                <div className={styles.cardTop}>
                  <div className={styles.roleIcon}>
                    <ShieldCheck size={20} />
                  </div>
                  <div className={styles.cardTitleWrap}>
                    <div className={styles.cardName}>
                      {prettyRole(role.name)}
                      {role.isSystem && (
                        <span className={styles.systemBadge}>
                          <Lock size={11} /> System
                        </span>
                      )}
                    </div>
                    <div className={styles.cardDesc}>
                      {role.description || "No description"}
                    </div>
                  </div>
                </div>

                <div className={styles.cardStats}>
                  <div className={styles.stat}>
                    <Layers size={14} />
                    <span>{hierarchyLabel(role.hierarchyLevel)}</span>
                  </div>
                  <div className={styles.stat}>
                    <KeyRound size={14} />
                    <span>
                      <b>{permCount}</b> permissions
                    </span>
                  </div>
                  <span
                    className={`${styles.statusBadge} ${
                      role.status === "ACTIVE" ? styles.stActive : styles.stInactive
                    }`}
                  >
                    {prettyRole(role.status || "")}
                  </span>
                </div>

                <div className={styles.cardActions}>
                  <button
                    className={styles.viewBtn}
                    onClick={() => openEdit(role)}
                  >
                    {role.isSystem ? (
                      <>
                        <Eye size={15} /> View
                      </>
                    ) : (
                      <>
                        <Pencil size={15} /> Edit
                      </>
                    )}
                  </button>
                  <button
                    className={styles.delBtn}
                    disabled={role.isSystem}
                    title={role.isSystem ? "System roles cannot be deleted" : "Delete role"}
                    onClick={() => setRoleToDelete(role)}
                  >
                    <Trash2 size={15} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <RoleModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        role={editRole}
        readOnly={readOnly}
        permissions={permissions}
        onSaved={handleSaved}
      />

      <DeleteConfirmModal
        open={!!roleToDelete}
        onClose={() => setRoleToDelete(null)}
        title="Delete role"
        itemName={roleToDelete ? prettyRole(roleToDelete.name) : ""}
        onConfirm={handleConfirmDelete}
        loading={deleting}
      />
    </div>
  );
};

export default Roles;
